<template>
  <TheMainContent>
    <PlpContainer v-if="cmsSections?.section1?.length" class="mt-20" :is-wide>
      <BlockSection :section="cmsSections.section1" wrapper-type="div" />
    </PlpContainer>

    <PlpContainer class="mb-32" :is-wide>
      <div class="flex items-center">
        <Breadcrumb :breads="breadcrumb" trackingZone="landing_page" />

        <template v-if="popularRelatedLinks?.length">
          <div class="border-static-default-mid ml-16 h-24 border-l-2"></div>
          <RelativeLinks class="-mr-24 md:mr-0" :links="popularRelatedLinks" />
        </template>
      </div>

      <aside><TrustList spot="landing" /></aside>
    </PlpContainer>

    <div data-spec="body">
      <div data-spec="main">
        <div
          data-cnstrc-browse
          :data-cnstrc-num-results="productsSearchData.total"
          :data-cnstrc-result-page="page"
          data-test="product-results-refinement"
        >
          <PlpContainer :is-wide>
            <BlackFridayCountdown
              v-if="blackFridayCountdownData"
              class="mb-16 lg:mb-32"
              :date="blackFridayCountdownData.date"
              :label="blackFridayCountdownData.label"
            />
            <div class="mb-12 justify-between lg:mb-28 lg:flex">
              <div>
                <h1 class="heading-1 lg:punchline">
                  {{ title }}
                </h1>
                <Tagline v-if="tagline && !openFilter" :tagline />
              </div>
            </div>
          </PlpContainer>

          <PlpContainer v-if="cmsSections?.section2?.length" :is-wide>
            <BlockSection
              class="mb-56"
              :section="cmsSections.section2"
              wrapper-type="div"
            />
          </PlpContainer>

          <Finder
            v-if="isFinderEnabled"
            v-model:filters="filters"
            v-model:price="price"
            class="mb-16"
            :facets
            :price-facet
          />

          <PlpContainer id="results" :is-wide>
            <FacetsFilters
              v-if="facetLinks.length"
              class="mb-8"
              :links="facetLinks"
            />
            <TopFilters
              v-model:filters="filters"
              v-model:price="price"
              v-model:sort="sort"
              class="sticky -top-1 z-[1]"
              :facets
              :finder-enabled="isFinderEnabled"
              :is-pending
              :price-facet
              :search-nb-results="productsSearchData.total"
              :sort-options
            />
            <ProductComparisonResponsiveToggle
              v-if="isPageComparisonEnabled"
              class="relative z-[1]"
              :is-responsive-comparison-enabled
              @toggle="toggleResponsiveComparison"
            />

            <div class="mt-16 lg:flex lg:gap-16">
              <div class="w-full">
                <div class="mb-12 md:mb-16">
                  <ResultsCount :is-pending :total="productsSearchData.total" />

                  <FormattedMessage
                    v-if="withQualityAssuranceFee"
                    class="caption"
                    :definition="translations.qualityAssuranceFee"
                  >
                    <template #qualityAssuranceFeeLink>
                      <ServiceFeeModal triggerLinkClass="caption-link" />
                    </template>
                  </FormattedMessage>
                </div>

                <SwapPill
                  v-if="isBuybackEnabled"
                  :class="{ 'lg:hidden': !showPlpBlocks }"
                />

                <NoResults
                  v-if="productsSearchData.products.length === 0"
                  class="my-32"
                  data-spec="body"
                />
                <ResultGrid
                  v-else
                  :article="articlesData.articles[0]"
                  :battery-block
                  class="mt-8 md:mt-32"
                  :comparison-products
                  :is-comparison-add-product-disabled
                  :is-comparison-enabled="isPageComparisonEnabled"
                  :is-filtered
                  :is-pending
                  :is-responsive-comparison-enabled
                  :is-wide
                  :marketingContent
                  :page-input="page"
                  :plp-blocks
                  :products="productsSearchData.products"
                  :reviews-count="rates?.count || 0"
                  :reviews-rate="rates?.averageRate || 0"
                  with-iphone-17
                  with-payment-advertisement
                  @toggle-comparison-product="
                    toggleComparisonProductSelectionById
                  "
                >
                  <template v-if="cmsSections?.section3?.length" #cmsSection>
                    <BlockSection
                      class="!gap-y-16"
                      :rounded="true"
                      :section="cmsSections.section3"
                      wrapper-type="div"
                    />
                  </template>
                </ResultGrid>
              </div>
            </div>

            <Pagination
              v-model="page"
              :page-count="productsSearchData.pageCount"
              :with-html-links="tags?.includes('pagination_html_link')"
            />
          </PlpContainer>
        </div>
      </div>
    </div>

    <aside>
      <RevContainer>
        <Reviews :id class="mb-72" :rates :slug="routeSlug" :title />
      </RevContainer>

      <RevContainer v-if="cmsSections?.section4?.length">
        <BlockSection
          class="mb-56"
          :section="cmsSections.section4"
          wrapper-type="div"
        />
      </RevContainer>

      <RevContainer>
        <ArticleSection
          :carousels="articleCarousels"
          :is-pending="articleStatus === 'pending'"
        />

        <ProductTags v-if="linkBoosters.length" :links="linkBoosters" />
      </RevContainer>

      <RevContainer v-if="cmsSections?.section5?.length">
        <BlockSection
          class="mb-56"
          :section="cmsSections.section5"
          wrapper-type="div"
        />
      </RevContainer>

      <RevContainer v-if="!openFilter">
        <SEOText class="mb-72" :seo-text />
      </RevContainer>
    </aside>

    <FullscreenFilters
      v-model:filters="filters"
      v-model:price="price"
      v-model:sort="sort"
      :facets
      :price-facet
      :search-nb-results="productsSearchData.total"
      :sort-options
    />
    <ProductComparisonStickyBar
      v-if="isPageComparisonEnabled"
      :products="comparisonProducts"
      @clear="comparisonClear"
      @open-full-modal="comparisonOpenFullModal"
      @remove-product="comparisonRemoveProduct"
    />
    <!-- Modals -->
    <ClientOnly>
      <SatisfactionModal :name="REASSURANCE.SATISFACTION" />
      <WarrantyModal :name="REASSURANCE.WARRANTY" />
      <ProductComparisonModal
        v-if="isPageComparisonEnabled"
        :products="comparisonProducts"
        :products-specifications="comparisonProductsSpecifications"
        :products-specifications-loading="
          comparisonProductsSpecificationsLoading
        "
      />
    </ClientOnly>

    <QATranslationProbe :definition="translations.e2eTranslationCheck" />
  </TheMainContent>
</template>

<script lang="ts" setup>
import {
  createError,
  navigateTo,
  useAsyncData,
  useHead,
  useRequestURL,
  useRoute,
  useRuntimeConfig,
} from '#imports'
import { computed, ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { useRouteParams } from '@backmarket/nuxt-layer-base/composables/useRouteParams'
import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import BlockSection from '@backmarket/nuxt-layer-cms/BlockSection.vue'
import SatisfactionModal from '@backmarket/nuxt-layer-product-modals/SatisfactionModal.vue'
import ServiceFeeModal from '@backmarket/nuxt-layer-product-modals/ServiceFeeModal.vue'
import WarrantyModal from '@backmarket/nuxt-layer-product-modals/WarrantyModal.vue'
import ProductComparisonModal from '@backmarket/nuxt-layer-recommendation/ProductComparisonModal.vue'
import ProductComparisonResponsiveToggle from '@backmarket/nuxt-layer-recommendation/ProductComparisonResponsiveToggle.vue'
import ProductComparisonStickyBar from '@backmarket/nuxt-layer-recommendation/ProductComparisonStickyBar.vue'
import FullscreenFilters from '@backmarket/nuxt-layer-recommendation/filtering/FullscreenFilters.vue'
import TopFilters from '@backmarket/nuxt-layer-recommendation/filtering/TopFilters.vue'
import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useProductComparison } from '@backmarket/nuxt-layer-recommendation/useProductComparison'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import {
  useAddProductsCacheTagHeader,
  useAddServiceCacheTagHeader,
} from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import QATranslationProbe from '@backmarket/nuxt-module-i18n/QATranslationProbe.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevContainer } from '@ds/components/Container'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SwapPill from '~/scopes/buyback/swap/components/SwapPill/SwapPill.vue'
import { useFetchFormattedCMSSections } from '~/scopes/cms/composables/useFetchFormattedCMSSections'
import { useAddSectionsCacheTagHeader } from '~/scopes/cms/composables/useSetCMSPageCacheTagHeader'
import Breadcrumb from '~/scopes/navigation/components/Breadcrumb/Breadcrumb.vue'
import { useQualityServiceFeeEnabled } from '~/scopes/product-core/composables/useQualityServiceFeeEnabled/useQualityServiceFeeEnabled'
import {
  getSortOptions,
  useAlgoliaConfiguration,
} from '~/scopes/product-list/helpers/helpers'
import TrustList from '~/scopes/reassurance/components/TrustList/TrustList.vue'
import { REASSURANCE } from '~/scopes/reassurance/config/modals'
import { useLinkBooster } from '~/scopes/seo/composables/useLinkBooster'
import { useRegisterShoppingAssistantStructuredData } from '~/scopes/shopping-assistant/composables/useShoppingAssistantStructuredData'
import { getMetas } from '~/utils/seo/meta'
import { buildProductSchema, prepareJsonLdScript } from '~/utils/seo/schema'

import FacetsFilters from '../../components/FacetsFilters/FacetsFilters.vue'
import { ALLOWED_PLP_UUIDS } from '../../components/Finder/Finder.constants'
import Finder from '../../components/Finder/Finder.vue'
import NoResults from '../../components/NoResults/NoResults.vue'
import Pagination from '../../components/Pagination/Pagination.vue'
import PlpContainer from '../../components/PlpContainer/PlpContainer.vue'
import ResultGrid from '../../components/ResultGrid/ResultGrid.vue'
import ResultsCount from '../../components/ResultsCount/ResultsCount.vue'
import { useBatteryBlock } from '../../composables/useBatteryBlock/useBatteryBlock'
import { useFetchOpenFacets } from '../../composables/useFetchOpenFacets'
import { useFiltersFromUrl } from '../../composables/useFiltersFromUrl'
import { useOpenFilter } from '../../composables/useOpenFilter'
import { usePlpBlocks } from '../../composables/usePlpBlocks/usePlpBlocks'
import { useProductsSearch } from '../../composables/useProductsSearch/useProductsSearch'
import { useSearchState } from '../../composables/useSearchState/useSearchState'
import { buildLinkBoosterSourceUrl } from '../../utils/buildLinkBoosterSourceUrl'

import {
  BLACK_FRIDAY_END_DATE,
  BLACK_FRIDAY_START_DATE,
} from './ProductListPage.constants'
import translations from './ProductListPage.translations'
import ArticleSection from './components/ArticleSection/ArticleSection.vue'
import BlackFridayCountdown from './components/BlackFridayCountdown/BlackFridayCountdown.vue'
import ProductTags from './components/ProductTags/ProductTags.vue'
import RelativeLinks from './components/RelativeLinks/RelativeLinks.vue'
import Reviews from './components/Reviews/Reviews.vue'
import SEOText from './components/SEOText/SEOText.vue'
import Tagline from './components/Tagline/Tagline.vue'
import { useArticles } from './composables/useArticles'
import { useProductListPage } from './composables/useProductListPage'
import { useProductListRating } from './composables/useProductListRating'

const { uuid: id, slugV2: routeSlug } = useRouteParams()
const route = useRoute()
const i18n = useI18n()
const { features } = useMarketplace()
const { isBuybackEnabled } = useBuybackEnabled()

const experiments = useExperiments()
const isWide =
  experiments['experiment.plpWideContainer'] === 'withWideContainer'

const { data: rates } = useProductListRating(id)
const { data: articlesData, status: articleStatus } = useArticles()
const articleCarousels = computed(() => [
  {
    articles: articlesData.value.articles.slice(1),
    title: i18n(translations.buyingGuides),
  },
  {
    articles: articlesData.value.relatedComparePages,
    title: i18n(translations.relatedComparePagesTitle),
  },
])

const [
  { data: plpConfiguration },
  { data: searchConfig },
  { data: cmsSections },
  { data: openFacets, error: openFacetsError },
] = await Promise.all([
  useProductListPage(id),
  useAlgoliaConfiguration('product-landing', id),
  useFetchFormattedCMSSections({ timeout: 2000 }),
  useFetchOpenFacets(id),
])

if (plpConfiguration.value === undefined) {
  throw createError({
    status: 404,
    statusText: 'Not found',
    fatal: true,
  })
}

const {
  title: pageTitle,
  tagline,
  seoText,
  marketingContent,
  popularRelatedLinks,
  breadcrumb: configBreadcrumb,
  seoTitle,
  seoDescription,
  displayedTitle,
  links,
  tags,
  slug,
} = plpConfiguration.value

const facets = ref<Facet[]>([])

const {
  openFilter,
  redirectInvalidFilters,
  redirectThinResults,
  watchOpenFilter,
  title,
  breadcrumb,
} = useOpenFilter(openFacets, openFacetsError, {
  pageTitle,
  configBreadcrumb,
  facets,
  configFacetKeys: computed(
    () => searchConfig.value?.facets.map((f) => f.name) ?? [],
  ),
})

if (slug && slug !== routeSlug) {
  const redirect: RouteLocationRaw = {
    name: route.name,
    query: route.query,
    hash: route.hash,
    params: { ...route.params, slugV2: slug },
  }

  await navigateTo(redirect, { redirectCode: 301 })
}

await redirectInvalidFilters()

if (searchConfig.value === undefined) {
  throw createError({
    status: 500,
    statusText: 'Unable to retrieve search configuration',
    fatal: true,
  })
}

const isPageComparisonEnabled = searchConfig.value.isComparisonEnabled
const sortOptions = getSortOptions(searchConfig.value.indexes)

const { isQualityServiceFeeEnabled: withQualityAssuranceFee } =
  useQualityServiceFeeEnabled()

const { filters, sort, price, page } = useSearchState(
  searchConfig.value,
  openFacets.value,
)

const isFiltered = computed(
  () =>
    Object.values(filters).some((values) => values.length > 0) ||
    price.value !== undefined,
)

const { batteryBlock } = useBatteryBlock(() => plpConfiguration.value?.slug)
const { plpBlocks } = usePlpBlocks(() => plpConfiguration.value?.slug)

const showPlpBlocks = computed(() => Boolean(plpBlocks.value))

const disjunctiveFacetKeys = computed(() =>
  openFilter.value ? [openFilter.value.key] : [],
)

const { data: productsSearchData } = await useProductsSearch(
  id,
  searchConfig.value,
  {
    filtersInput: filters,
    priceInput: price,
    sortInput: sort,
    pageInput: page,
    disjunctiveFacetKeys,
  },
  { feature: 'plp' },
)

await redirectThinResults(productsSearchData.value.total)

// @TODO: Replace this with a real loading state
const isPending = ref(false)

const priceFacet = ref<UiPriceFacet | undefined>()

facets.value = productsSearchData.value.facets
priceFacet.value = productsSearchData.value.priceFacet

useAddProductsCacheTagHeader({
  products: productsSearchData.value.products,
})

useAddSectionsCacheTagHeader(cmsSections.value?.id)

useAddServiceCacheTagHeader()

useFiltersFromUrl(filters, price, page, {
  openFacets: openFacets.value,
})

watchOpenFilter(filters)

const {
  products: comparisonProducts,
  isAddProductDisabled: isComparisonAddProductDisabled,
  isResponsiveComparisonEnabled,
  toggleProductSelectionById: toggleComparisonProductSelectionById,
  toggleResponsiveComparison,
  clear: comparisonClear,
  openFullModal: comparisonOpenFullModal,
  removeProduct: comparisonRemoveProduct,
  productsSpecifications: comparisonProductsSpecifications,
  productsSpecificationsLoading: comparisonProductsSpecificationsLoading,
} = useProductComparison({
  pageProducts: computed(() => productsSearchData.value.products),
})

const linkBoosterSourceUrl = computed(() =>
  buildLinkBoosterSourceUrl(route.path, openFilter.value),
)

const { data: linkBoosterLists } = useAsyncData(
  getAsyncDataKey('product-list-link-booster', {
    path: linkBoosterSourceUrl.value,
  }),
  () =>
    useLinkBooster().fetchLinkBoosterLists({
      sourceUrl: linkBoosterSourceUrl.value,
      listTypes: ['plp_linkboosters', 'plp_facet_linkboosters'],
      timeout: 500,
    }),
  { lazy: true, watch: [linkBoosterSourceUrl] },
)

const MAX_FACET_LINKS = 10

const allFacetLinks = computed(
  () => linkBoosterLists.value?.plp_facet_linkboosters ?? [],
)
const facetLinks = computed(() => allFacetLinks.value.slice(0, MAX_FACET_LINKS))
const linkBoosters = computed(() => [
  ...(linkBoosterLists.value?.plp_linkboosters ?? []),
  ...allFacetLinks.value.slice(MAX_FACET_LINKS),
])

const pageNumber = route.query.p
const seoTitleWithFilter = openFilter.value
  ? `${seoTitle} • ${openFilter.value.label}`
  : seoTitle
const metaTitle = pageNumber
  ? i18n(translations.seoTitle, { title: seoTitleWithFilter, pageNumber })
  : seoTitleWithFilter
const metaImageUrl = '/img/plp/ogImage.png'

const metaDescription =
  seoDescription ||
  i18n(translations.metaDescription, {
    coolingOffDays: features.coolingOffDays,
    productType: displayedTitle,
    warrantyDuration: features.warranty.duration,
  })
const { protocol, hostname } = useRequestURL()

function buildCanonicalHref() {
  const base = `${protocol}//${hostname}${route.path}`
  const filter = openFilter.value

  return filter
    ? `${base}?${filter.key}=${encodeURIComponent(filter.value)}`
    : base
}

const {
  public: { COMPANY_NAME },
} = useRuntimeConfig()

const isFinderEnabled = computed(
  () =>
    experiments['experiment.macbookPLPFinder'] === 'enabled' &&
    ALLOWED_PLP_UUIDS.includes(id),
)

const productSchema = computed(() =>
  buildProductSchema({
    title: metaTitle,
    description: metaDescription,
    rate: rates.value ?? { averageRate: NaN, count: NaN },
    image: metaImageUrl,
    isOutOfStock: productsSearchData.value.products.length === 0,
    price: productsSearchData.value.products[0]?.price,
    companyName: COMPANY_NAME,
    brand: COMPANY_NAME,
  }),
)
useHead(() => ({
  title: metaTitle,
  titleTemplate: `%s | ${COMPANY_NAME}`,
  meta: getMetas({
    title: `${metaTitle} | ${COMPANY_NAME}`,
    description: metaDescription,
    image: metaImageUrl,
    'og:type': 'product',
  }),
  link: [
    ...(openFilter.value
      ? []
      : Object.values(links).map((item) => ({
          rel: 'alternate',
          hreflang: item.params.locale,
          href: item.href,
        }))),
    {
      href: buildCanonicalHref(),
      rel: 'canonical',
    },
  ],
  script: [prepareJsonLdScript(productSchema.value)],
}))

useRegisterShoppingAssistantStructuredData(productSchema)

const blackFridayCountdownData = computed(() => {
  if (tags?.includes('black_friday_start_countdown')) {
    return {
      label: i18n(translations.blackFridayStartLabel),
      date: BLACK_FRIDAY_START_DATE,
    }
  }
  if (tags?.includes('black_friday_end_countdown')) {
    return {
      label: i18n(translations.blackFridayEndLabel),
      date: BLACK_FRIDAY_END_DATE,
    }
  }

  return null
})
</script>
